import React, { useState } from 'react';
import styled from 'styled-components';
import { useBoolean } from 'lib/hooks';
import { MemberEntity } from 'model/api/GetMvpds';
import { handleTrackEvent, Events } from 'lib/analytics/trackCalls';
import { Brand } from 'constants/index';
import SearchInput from '../SearchInput';

export enum ProviderSearchListElementsText {
  searchAllProvidersInput = 'Search All Providers',
  dontHaveProviderLink = `I don't have a TV Provider`,
  noProvidersFoundText = 'No providers found',
}

export type Props = {
  mvpdList: MemberEntity[] | null | undefined;
  handleMVPDSelect: (adobePassId: string) => void;
};

const LINKS = {
  goWatchLink:
    process.env.NEXT_PUBLIC_BRAND === Brand.SPORTS
      ? 'https://www.foxsports.com/gowatch'
      : 'https://gowatchfox.com',
};

const ProviderSearchList = ({ mvpdList = [], handleMVPDSelect }: Props): JSX.Element | null => {
  const [filteredList, setFilteredList] = useState<MemberEntity[] | null | undefined>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    value: isFocused,
    setTrue: setIsFocusedTrue,
    setFalse: setIsFocusedFalse,
  } = useBoolean(false);

  const handleChange = (e: { target: { value: string }; preventDefault(): void }) => {
    e.preventDefault();
    setSearchQuery(e.target.value);
    const filteredMvpds: MemberEntity[] | null | undefined = mvpdList?.filter(
      (mvpd) => mvpd.name.toLowerCase().search(e.target.value.toLowerCase()) !== -1,
    );
    setFilteredList(filteredMvpds);
  };

  const handleDontHaveProviderClick = () => {
    handleTrackEvent({
      eventName: Events.BUTTON_LINK_SELECTED,
      props: {
        page_item_title: ProviderSearchListElementsText.dontHaveProviderLink,
        page_destination_url: LINKS.goWatchLink,
      },
    });
  };

  if (!mvpdList?.length) return null;

  return (
    <Search>
      {mvpdList && (
        <>
          <SearchInput
            searchQuery={searchQuery}
            setFilteredList={setFilteredList}
            setSearchQuery={setSearchQuery}
            handleChange={handleChange}
            setIsFocusedTrue={setIsFocusedTrue}
            setIsFocusedFalse={setIsFocusedFalse}
            isFocused={isFocused}
          />
          <ul className="providerList">
            {isFocused &&
              filteredList?.map((mvpd) => {
                const { id, adobePassId, name } = mvpd;
                return (
                  <li key={id} data-adobepassid={adobePassId}>
                    <a onClick={() => handleMVPDSelect(adobePassId)}>{name}</a>
                  </li>
                );
              })}
          </ul>
          {isFocused && filteredList?.length === 0 && (
            <div className="noResults">{ProviderSearchListElementsText.noProvidersFoundText}</div>
          )}
          <div className="notFoundContainer">
            <a
              className="notFoundLink"
              href={LINKS.goWatchLink}
              target="_blank"
              rel="noreferrer"
              onClick={handleDontHaveProviderClick}
            >
              {ProviderSearchListElementsText.dontHaveProviderLink}
            </a>
          </div>
        </>
      )}
    </Search>
  );
};

export default React.memo(ProviderSearchList);

const Search = styled.section`
  .providerList {
    max-width: 330px;
    margin: 0 auto;
    padding: 2rem 0 0 1rem;
    a {
      cursor: pointer;
    }
    li {
      margin: 0;
      display: block;
      width: 100%;
      max-width: 668px;
      height: 48px;
      text-align: left;
    }
  }

  .noResults {
    color: $Alto;
    font-family: Graphik, sans-serif;
    font-size: 18px;
    padding-left: 15px;
    font-style: italic;
    font-weight: 400;
    height: 50px;
    letter-spacing: -0.29px;
    line-height: 50px;
    width: 330px;
    text-align: left;
    margin: 0 auto;
  }
  .notFoundContainer {
    margin-bottom: 20px;
    font-size: 14px;
    font-weight: 400;
    height: 20px;
    letter-spacing: 0;
    line-height: 20px;
    text-align: center;
    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    color: $white;
    font-family: Graphik, sans-serif;
    .notFoundLink {
      color: $white;
      text-decoration: none;
      opacity: 0.7;
    }
  }
`;
